import React from 'react';
import { Link } from 'react-router-dom';
import type { PassportSummary } from '../../services/api/passport';
import { nextVehicleMilestone } from './passportFeedUtils';

type Props = {
  summary: PassportSummary | null;
  year: number;
};

/**
 * Editorial Passport hero — one dominant number, supporting presence metrics.
 * Not an admin stat grid.
 */
export const PassportHero: React.FC<Props> = ({ summary, year }) => {
  const cars = summary?.vehicles_encountered_count ?? 0;
  const events = summary?.events_encountered_count ?? 0;
  const collections = summary?.collections_completed ?? 0;
  const encounters = summary?.encounter_count ?? 0;
  const milestone = nextVehicleMilestone(cars);

  const dominant =
    cars > 0
      ? { value: cars, label: cars === 1 ? 'Build discovered' : 'Builds discovered' }
      : encounters > 0
        ? { value: encounters, label: encounters === 1 ? 'Encounter logged' : 'Encounters logged' }
        : { value: 0, label: 'Your collection starts here' };

  return (
    <header className="relative overflow-hidden rounded-2xl border border-white/[0.08] bg-gradient-to-br from-[#121212] via-[#0c0c0c] to-[#080808] px-5 py-7 sm:px-8 sm:py-9 mb-8">
      <div
        className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-amber-500/[0.07] blur-3xl"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -left-10 bottom-0 h-40 w-40 rounded-full bg-sky-500/[0.05] blur-3xl"
        aria-hidden
      />

      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
        Bilt:Spec Passport
      </p>
      <h1 className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white">
        Your Passport
      </h1>
      <p className="mt-2 max-w-xl text-sm text-zinc-400 leading-relaxed">
        A private record of the builds, events, and places you&apos;ve collected. History over
        score.
      </p>

      <div className="mt-8 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-5xl sm:text-6xl font-semibold tabular-nums tracking-tight text-white">
            {dominant.value}
          </p>
          <p className="mt-1 text-sm text-zinc-400">{dominant.label}</p>
          {milestone && cars > 0 ? (
            <p className="mt-3 text-xs text-amber-200/80">
              {milestone.remaining} more until {milestone.label}
            </p>
          ) : null}
        </div>

        {encounters > 0 ? (
          <ul className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-zinc-400 sm:justify-end">
            {events > 0 ? (
              <li>
                <span className="text-white tabular-nums font-medium">{events}</span>{' '}
                {events === 1 ? 'event' : 'events'}
              </li>
            ) : null}
            {collections > 0 ? (
              <li>
                <span className="text-white tabular-nums font-medium">{collections}</span>{' '}
                {collections === 1 ? 'collection complete' : 'collections complete'}
              </li>
            ) : null}
            {summary && summary.campaigns_joined > 0 ? (
              <li>
                <span className="text-white tabular-nums font-medium">
                  {summary.campaigns_joined}
                </span>{' '}
                {summary.campaigns_joined === 1 ? 'hunt joined' : 'hunts joined'}
              </li>
            ) : null}
            <li>
              <Link
                to={`/passport/recap?year=${year}`}
                className="text-zinc-300 underline-offset-4 hover:text-white hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-400/60"
              >
                {year} recap
              </Link>
            </li>
          </ul>
        ) : null}
      </div>
    </header>
  );
};

export default PassportHero;
